import React from 'react';
import { View, StyleSheet } from 'react-native';
import { Text } from '@/components/common/text';
import { REDESIGN_TYPOGRAPHY } from '@/constants/residents/homeConstants';
import { useThemeColors } from '@/hooks/residents/useThemeColors';
import { StatusTimelineNode } from '@/utils/residents/maintenance/statusTimeline';

interface HorizontalStatusTimelineProps {
  nodes: StatusTimelineNode[];
  showDates?: boolean;
}

const DOT = 10;

/**
 * Horizontal variant of the status timeline: dots + connecting rails on one
 * line, labels (and optional dates) beneath. Same node model as the vertical
 * StatusTimeline (buildStatusTimeline is the single source of nodes).
 * Geometry assumes exactly the 3 nodes buildStatusTimeline produces — the label row's equal flex cells only line up with the dot rail at N=3.
 */
export const HorizontalStatusTimeline: React.FC<HorizontalStatusTimelineProps> = ({
  nodes,
  showDates = false,
}) => {
  const { colors } = useThemeColors();

  const a11y = nodes
    .map((n) => `${n.label}${n.state === 'active' ? ' (current)' : ''}${showDates && n.date ? `, ${n.date}` : ''}`)
    .join('; ');

  return (
    <View accessible accessibilityLabel={`Status timeline: ${a11y}`}>
      <View style={styles.rail}>
        {nodes.map((node, index) => (
          <React.Fragment key={node.key}>
            <View
              style={[
                styles.dot,
                { backgroundColor: node.state === 'upcoming' ? colors.glass.buttonBorder : node.color },
                node.state === 'active' && [styles.dotActive, { borderColor: node.color }],
              ]}
            />
            {index < nodes.length - 1 && (
              <View
                style={[
                  styles.line,
                  { backgroundColor: node.state === 'done' ? node.color : colors.glass.buttonBg },
                ]}
              />
            )}
          </React.Fragment>
        ))}
      </View>
      <View style={styles.labels}>
        {nodes.map((node, index) => (
          <View
            key={node.key}
            style={[
              styles.labelCell,
              index === 0 && styles.labelFirst,
              index === nodes.length - 1 && styles.labelLast,
            ]}
          >
            <Text
              numberOfLines={2}
              style={[
                styles.label,
                { color: node.state === 'upcoming' ? colors.text.dim : colors.text.primary },
                index === 0 && styles.textLeft,
                index === nodes.length - 1 && styles.textRight,
              ]}
            >
              {node.label}
            </Text>
            {showDates && node.date ? (
              <Text
                numberOfLines={1}
                style={[
                  styles.date,
                  { color: colors.text.muted },
                  index === 0 && styles.textLeft,
                  index === nodes.length - 1 && styles.textRight,
                ]}
              >
                {node.date}
              </Text>
            ) : null}
          </View>
        ))}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  rail: { flexDirection: 'row', alignItems: 'center' },
  dot: { width: DOT, height: DOT, borderRadius: DOT / 2 },
  dotActive: { borderWidth: 2.5, backgroundColor: 'transparent', width: DOT + 2, height: DOT + 2, borderRadius: (DOT + 2) / 2 },
  line: { flex: 1, height: 2, marginHorizontal: 3 },
  labels: { flexDirection: 'row', marginTop: 6 },
  labelCell: { flex: 1 },
  labelFirst: { alignItems: 'flex-start' },
  labelLast: { alignItems: 'flex-end' },
  label: { ...REDESIGN_TYPOGRAPHY.caption, lineHeight: 16, textAlign: 'center' },
  date: { ...REDESIGN_TYPOGRAPHY.caption, fontSize: 10, lineHeight: 13, textAlign: 'center' },
  textLeft: { textAlign: 'left' },
  textRight: { textAlign: 'right' },
});
